import { Component, Inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SpecialModule } from 'lib/modules/special.module';
import { CmsPartialComponentClass } from 'lib/components/cms/cms-partial-component.class';
import { RouterService } from 'lib/services/router.service';
import { WebpService } from 'lib/services/webp.service';
import { CategoryService } from 'lib/services/category/category.service';
import { BootstrapMarketoService } from 'lib/services/bootstrap/bootstrap-marketo.service';
import { ConfiguratorService } from 'lib/services/configuration/configurator.service';
import { isConfigurationLive } from 'lib/services/configuration/configuration-status.util';
import { WebstoreProductService } from 'lib/services/webstore-product/webstore-product.service';
import { getMpnFromProductUrl, resolveConfiguratorMpn, resolveDisplayMpn } from 'lib/services/webstore-product/product-url.util';
import get from 'lodash-es/get';
import * as feather from 'feather-icons';

@Component({
    selector: 'cms-partial-category-top-feature-solutions',
    templateUrl: './feature-solutions.component.html',
    styleUrls: ['./feature-solutions.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [CommonModule, RouterModule, SpecialModule],
})
export class FeatureSolutionsComponent extends CmsPartialComponentClass implements OnInit {
    @Input() featureSolutionsRow: any;
    @Input() solutionUrl: string;
    @Input() grayBackgroundRows: any = [];
    getWebpImg: (src: string) => string;

    constructor(
        @Inject('environment') environment: any,
        public bootstrapMarketoService: BootstrapMarketoService,
        routerService: RouterService,
        webpService: WebpService,
        location: Location,
        categoryService: CategoryService,
        public configuratorService: ConfiguratorService,
        private webstoreProductService: WebstoreProductService
    ) {
        super({
            dependencies: {
                bootstrapMarketoService,
                environment,
                routerService,
                location,
                categoryService,
                configuratorService,
            },
        });
        this.getWebpImg = (src: string) => webpService.getWebpImg(src);
        // feather.replace();
    }

    async ngOnInit() {
        // feather.replace();
        if (this.featureSolutionsRow.feature_solutions.feature_solutions[0].feature_solutions) {
            const solutions = this.featureSolutionsRow.feature_solutions.feature_solutions[0].feature_solutions;

            // Synchronous, before any await, so the first render already has these resolved.
            solutions.forEach((solution: any) => {
                this.resolveMpns(solution);
                this.resolveCustomNotes(solution);
            });

            const productsMap = new Map();

            // First, map over all the products. Keyed on the URL's MPN, never the override, so
            // the spec lines always describe the product the card actually links to.
            for (const solution of solutions) {
                const mpn = solution.productMpn;

                // Not a product page, so there is no product to fetch. Also avoids an
                // unfiltered request pulling back the whole catalogue.
                if (!mpn) continue;

                try {
                    const product = await this.webstoreProductService.getProductByMpnComponentConfig(mpn);
                    if (product == null) {
                        continue; // Skip to the next iteration
                    }
                    const newProduct = { ...product };

                    const newHighlights = this.formatHighlights(newProduct?.highlights);
                    newProduct.highlights = newHighlights;

                    productsMap.set(mpn, newProduct);
                } catch (error) {
                    console.error(`Error fetching product for MPN ${mpn}:`, error);
                } finally {
                    // feather.replace();
                }
            }

            // Then, place the products on the correct solution
            await solutions.forEach(async (solution) => {
                const configuratorMpn = solution.configuratorMpn;
                const product = productsMap.get(solution.productMpn);
                solution.isBlackListed = this.configuratorService.isBlacklisted(configuratorMpn);

                if (product) {
                    solution.product = product;
                    solution.product.price = 0; // Prevent usage of default product pricing
                } else {
                    console.log(`No product found for solution with MPN ${solution.productMpn}`);
                }

                // The Configure button follows the override, and only the override.
                const forConfigurator = await this.findLiveConfig(configuratorMpn);
                solution.exxcomIsActive = !!forConfigurator;

                // The price prefers the URL's MPN, so an override never costs the card its
                // price, and falls back to the override's own configuration.
                const forPrice =
                    (solution.productMpn === configuratorMpn ? forConfigurator : await this.findLiveConfig(solution.productMpn)) ||
                    forConfigurator;

                if (forPrice && solution.product) {
                    const spcConfigs = await this.configuratorService.getConfigurationWithBasePrice('spc', forPrice.mpn);
                    const spcConfig = spcConfigs?.find((config) => config.mpn?.toLowerCase() == forPrice.config.mpn?.toLowerCase());
                    solution.product.price = spcConfig?.referenceId?.optimizedConfigurations?.good?.spcPriceTotal;
                }
            });
            // feather.replace();
        } else {
            console.log('No feature solutions to process.');
        }
    }

    /**
     * The first live configuration for an MPN, together with the MPN that found it so the
     * follow-up price call can reuse it. Returns null for a falsy MPN, which also stops an
     * unfiltered request pulling back every spc configuration.
     *
     * Matching is case-insensitive, mirroring the product page — a casing difference used to
     * find no config and silently hide the Configure button.
     */
    private async findLiveConfig(mpn: string) {
        if (!mpn) {
            return null;
        }
        const configs = await this.configuratorService.getConfigurationByUrlComponent('spc', mpn, null, null, true, true);
        const config = configs?.find(
            (c: any) => c?.mpn && mpn.toLowerCase().startsWith(c.mpn.toLowerCase()) && isConfigurationLive(c)
        );
        return config ? { mpn, config } : null;
    }

    /**
     * Three MPNs, three jobs. The dividing line: product identity comes from the URL, while the
     * configurator target follows the editor's override.
     *
     *  - productMpn is the URL's MPN, never the override, and fetches the product whose spec
     *    lines the card renders. Product information describes the page the card links to, so it
     *    must not depend on Use Custom Part Number.
     *  - configuratorMpn drives the configuration lookup, the price and the Configure href. It
     *    has no authored fallback, so a URL that is not a product page yields no button.
     *  - displayMpn is what the card shows and a quote carries: an editor override, else the
     *    URL's MPN, else whatever was authored. Blank means the MPN line is hidden.
     */
    resolveMpns(solution: any) {
        const urlMpn = getMpnFromProductUrl(
            get(solution, 'spec_link.routerLink', ''),
            this.environment.productUrlRegex,
            this.environment.productUrlBrandPosition
        );
        const authoredMpn = get(solution, 'part_number', '');
        const useCustomMpn = get(solution, 'use_custom_part_number', false);
        solution.productMpn = urlMpn;
        solution.displayMpn = resolveDisplayMpn(urlMpn, authoredMpn, useCustomMpn);
        solution.configuratorMpn = resolveConfiguratorMpn(urlMpn, authoredMpn, useCustomMpn);
    }

    /**
     * Custom notes flattened to a list of non-empty strings.
     *
     * Reads both shapes on purpose. custom_notes was a single group holding a repeating text
     * field, and becomes a repeating group holding a single text field — the nesting had to be
     * inverted because a `multiple` scalar inside a singular group inside a repeating group is
     * what broke copy/pasting Feature Solutions between entries. Tolerating either shape means
     * this deploy and the Contentstack change do not have to land in lockstep.
     */
    resolveCustomNotes(solution: any) {
        const customNotes = get(solution, 'custom_notes', []);
        const notes = Array.isArray(customNotes)
            ? customNotes.map((note: any) => get(note, 'custom_note', '')) // new: repeating group
            : get(customNotes, 'custom_note', []); // old: repeating text
        // Coerced because the two schema properties are flipped one at a time in the CMS: saving
        // in between leaves a singular group holding a singular text, so this arrives as a string.
        const noteList = Array.isArray(notes) ? notes : [notes];
        solution.customNotes = noteList.map((note: any) => (note || '').trim()).filter(Boolean);
    }

    formatHighlights(highlights: string) {
        if (!highlights || highlights.length < 2) return '';
        // Add a line break after each colon
        let formattedHighlights = highlights.split(':').join(':\n');

        // Wrap text after <li> and before \n in a span with class "li-title"
        formattedHighlights = formattedHighlights.replace(/<li>([^:\n]+):/g, '<li><span class="li-title">$1</span>:');

        // Add class "list" to the ul
        formattedHighlights = formattedHighlights.replace(/<ul>/g, '<ul class="list">');

        return formattedHighlights;
    }

    splitWord(title: string): { firstPart: string; lastPart: string } {
        const words = title.trim().split(' ');
        const lastWord = words.pop() || '';
        const firstPart = words.join(' ');
        return { firstPart, lastPart: lastWord };
    }

    encodeConfiguratorUrl(baseUrl: string, mpn: string): string {
        return baseUrl + encodeURIComponent(mpn);
    }
}
